import React, { useEffect, useState } from 'react';
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getOrderDetail } from "../../services/orderService";
import PageLayout from "../../components/PageLayout";
import { allStatuses } from "../../utils/Constants";

interface OrderData {
  [key: string]: string | number | boolean | null | undefined;
}

const Status: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation();

  // Special statuses that are alternatives to the regular flow
  const specialStatuses = ['PASSENGER_CANCEL', 'DRIVER_CANCEL', 'FINISHED_EARLY', 'OTHER_FINISHED'];

  // Find current status index
  const currentStatusIndex = allStatuses.indexOf(status);
  const isSpecialStatus = specialStatuses.includes(status);

  return (
    <div className="bg-gray-800 p-3 sm:p-4 rounded-lg">
      <p className="text-sm text-gray-400 font-medium mb-3 text-left">{t('status')}:</p>

      {isSpecialStatus ? (
        <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-2 sm:p-3 flex items-center">
          <div className="h-3 w-3 sm:h-4 sm:w-4 rounded-full bg-yellow-500 mr-2"></div>
          <p className="text-xs sm:text-sm text-yellow-400 font-medium">{t(status)}</p>
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          <div className="bg-green-900/30 border border-green-700/50 rounded-lg p-2 sm:p-3 flex items-center">
            <div className="h-3 w-3 sm:h-4 sm:w-4 rounded-full bg-green-500 mr-2"></div>
            <p className="text-xs sm:text-sm text-green-400 font-medium truncate">{t(status)}</p>
          </div>

          {/* Progress timeline */}
          <div className="relative pt-2 max-h-[300px] overflow-y-auto pr-2">
            <div className="absolute left-2 sm:left-3 top-2 bottom-4 w-0.5 bg-white/60"></div>
            {allStatuses.map((stepStatus, index) => {
              const isCompleted = index <= currentStatusIndex;
              const isCurrent = index === currentStatusIndex;

              return (
                <div key={stepStatus} className="relative flex items-center mb-3 sm:mb-4 pl-8 sm:pl-12">
                  <div className={`absolute left-2 sm:left-3 h-4 w-4 sm:h-5 sm:w-5 rounded-full ${
                    isCurrent ? 'bg-blue-500 ring-2 ring-blue-300 ring-opacity-30' :
                      isCompleted ? 'bg-green-500' : 'bg-gray-600'
                  } -ml-2 sm:-ml-2.5`}></div>
                  <p className={`text-xs ${
                    isCurrent ? 'text-blue-400 font-medium' :
                      isCompleted ? 'text-green-400' : 'text-gray-500'
                  } truncate`}>
                    {t(stepStatus)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

interface OrderDetailProps {
  orderData: Record<string, any>;
}

const OrderDetail: React.FC<OrderDetailProps> = ({ orderData }) => {
  const { t } = useTranslation();

  // List of keys to display
  const keysToShow = ['orderId', 'startAddress', 'endAddress', 'passengers', 'luggage', 'useCarTime'];

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-white border-b border-gray-700 pb-2">
        {t('OrderInformation')}
      </h2>

      <div className="grid grid-cols-1 gap-3">
        {keysToShow.map((key) => (
          orderData[key] !== undefined && (
            <div key={key} className="bg-gray-800 p-3 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                <p className="text-sm text-gray-400 font-medium mb-1 sm:mb-0">{t(key)}:</p>
                <p className="text-sm font-medium text-white break-words sm:ml-2 sm:text-right">{String(orderData[key])}</p>
              </div>
            </div>
          )
        ))}
        <Status status={orderData.status as string}/>
      </div>
    </div>
  );
};



const OrderDetailPage = () => {
  const [searchParams] = useSearchParams();
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const orderId = searchParams.get("orderId");
  const { t } = useTranslation();

  useEffect(() => {
    const fetchOrderData = async (orderId: string) => {
      try {
        const data: OrderData = await getOrderDetail(orderId);
        setOrderData(data);
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("An unknown error occurred");
        }
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      // console.log(orderId);
      fetchOrderData(orderId);
    } else {
      setError("Missing orderId in URL");
      setLoading(false);
    }
  }, [orderId]);

  return (
    <PageLayout isBooking={true} isLogin={true}>
      <div className="flex justify-center items-center w-full px-4 sm:px-0 pt-20">
        <main className="flex flex-col items-center w-full sm:w-[90%] md:w-[85%] lg:w-[80%]">
          <div className="relative z-10 bg-gray-900 rounded-xl shadow-2xl w-[90%] max-w-[320px] sm:max-w-md mx-auto animate-in fade-in zoom-in-95 duration-300">
            <div className="p-4 sm:p-6">
              {loading && <p>{t('OrderLoading')}</p>}
              {/*{error && <p>Error: {error}</p>}*/}
              {!loading && !error && !orderData && <p>{t('OrderNotFound')}</p>}
              {!loading && !error && orderData && <OrderDetail orderData={orderData} />}
            </div>
          </div>
        </main>
      </div>
    </PageLayout>
  )
};

export default OrderDetailPage;